import React, { Component } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import Dismiss from '../../__partials/dismiss';
import Button from '../../buttons/button';
import Indicator from '../__partials/indicator';

import './style.scss';

const STATES = {
  Mounted: 'Mounted', // Component is Mounted but the toast has not enterred
  Entering: 'Entering', // The Toast notification is in the act of entering
  Active: 'Active', // The toast is at 100% opacity (most commonly set when hovering)
  Resting: 'Resting', // The toast is not being interacted with and is at less than 100% opacity
  Dismissing: 'Dismissing', // The toast is in the act of dismissing, allowing for the transition to complete
  Exiting: 'Exiting', // The toast has rested for the determined amount of time without interaction and is exiting
  Exited: 'Exited', // The toast has completely exited and onDismiss can be called
};

const isState = (state, ...states) => states.indexOf(state) > -1;
const isNotState = (state, ...states) => !isState(state, ...states);

const REST_TIMEOUT_DURATION = 4000;
const INITIAL_REST_TIMEOUT_DURATION = 300;

export default class Toast extends Component {
  constructor() {
    super();

    this.state = {
      toastState: STATES.Mounted,
    };
  }

  componentDidMount() {
    // Listen for the start and end of the entrance animation. We need to set
    // state and timers based on when the animation starts and ends.
    this.toastEl.addEventListener('animationstart', this.enterStarted);
    this.toastEl.addEventListener('animationend', this.enterEnded);

    // Listen for the end of transitions. Certain states need to be set at the end of certain transitions
    this.toastEl.addEventListener('transitionend', this.handleTransitionEnd);
  }

  componentWillUpdate(nextProps, nextState) {
    const { toastState } = this.state;

    // After entering and going active, we will fade the Toast after 300ms
    if (isState(toastState, STATES.Entering) && isState(nextState.toastState, STATES.Active)) {
      this.startTimeoutForInitialRest();
    }

    // When starting to rest, start the resting timeout
    if (isNotState(toastState, STATES.Resting) && isState(nextState.toastState, STATES.Resting)) {
      this.startRestTimeout();
    }
  }

  componentWillUnmount() {
    this.toastEl.removeEventListener('animationstart', this.enterStarted);
    this.toastEl.removeEventListener('animationend', this.enterEnded);

    this.toastEl.removeEventListener('transitionend', this.handleTransitionEnd);

    this.clearTimeouts(this.initialRestingTimeout, this.restTimeout);
  }

  handleTransitionEnd = () => {
    const { toastState } = this.state;

    // The transition that happens when in the following states are set
    // is the fade out transition. We need to finish exiting when that is done
    if (isState(toastState, STATES.Exiting, STATES.Dismissing)) {
      this.exitEnded();
    }
  };

  startRestTimeout() {
    // Don't auto-exit the toast if its critical type
    if (this.props.type === 'critical') return;

    this.restTimeout = setTimeout(() => {
      this.setState({
        toastState: STATES.Exiting,
      });
    }, REST_TIMEOUT_DURATION);
  }

  startTimeoutForInitialRest() {
    this.initialRestingTimeout = setTimeout(() => {
      this.setState({
        toastState: STATES.Resting,
      });
    }, INITIAL_REST_TIMEOUT_DURATION);
  }

  enterStarted = () => {
    this.setState({
      toastState: STATES.Entering,
    });
  };

  enterEnded = () => {
    this.setState({
      toastState: STATES.Active,
    });
  };

  exitEnded() {
    this.props.onDismiss();
  }

  onMouseEnter = () => {
    this.clearTimeouts(this.initialRestingTimeout, this.restTimeout);

    this.setState({
      toastState: STATES.Active,
    });
  };

  onMouseLeave = () => {
    // Don't rest if the user dismisses the toast
    if (isState(this.state.toastState, STATES.Dismissing)) return;

    this.setState({
      toastState: STATES.Resting,
    });
  };

  clearTimeouts(...timeouts) {
    timeouts.forEach(clearTimeout);
  }

  handleDismiss = () => {
    // Don't call set the state if its already set to Dismissing
    if (isState(this.state.toastState, STATES.Dismissing)) return;

    this.setState({
      toastState: STATES.Dismissing,
    });
  };

  handleAction = () => {
    this.handleDismiss();

    this.props.action.handler();
  };

  toastClassnames() {
    const { toastState } = this.state;

    return classnames('uni-toast', 'uni-toast--position', 'uni-env--light', {
      'uni-toast--is-active': isState(toastState, STATES.Active),
      'uni-toast--is-disappearing': isState(toastState, STATES.Exiting),
      'uni-toast--is-resting': isState(toastState, STATES.Resting),
      'uni-toast--is-dismissing': isState(toastState, STATES.Dismissing),
    });
  }

  render() {
    const { type, action, qaId, message } = this.props;

    let toastAction;
    if (action) {
      if (action.href) {
        toastAction = (
          <div className="uni-toast__actions">
            <a
              className="uni-btn--subtle uni-btn--small"
              href={action.href}
              target={action.target}
              data-qa-id={action.qaId}
            >
              {action.text}
            </a>
          </div>
        );
      } else {
        toastAction = (
          <div className="uni-toast__actions">
            <Button size="small" type="subtle" text={action.text} qaId={action.qaId} onClick={this.handleAction} />
          </div>
        );
      }
    }

    return (
      <div
        className={this.toastClassnames()}
        role="alert"
        onMouseEnter={this.onMouseEnter}
        onMouseLeave={this.onMouseLeave}
        data-qa-id={qaId}
        ref={e => (this.toastEl = e)}
      >
        <Indicator type={type === 'success' ? 'confirmation' : type} paddingSpace="half" />
        <div className="uni-toast__content">
          <h4 className="uni-toast__message">{message}</h4>
          {toastAction}
        </div>

        <Dismiss
          size="small"
          className="uni-toast__dismiss"
          qaId="toast-dismiss-action"
          hudlElement="toast-dismiss-action"
          onClick={this.handleDismiss}
        />
      </div>
    );
  }
}

Toast.propTypes = {
  type: PropTypes.oneOf(['information', 'warning', 'critical', 'confirmation', 'success']).isRequired,
  message: PropTypes.oneOfType([PropTypes.string, PropTypes.element]).isRequired,
  action: PropTypes.shape({
    type: PropTypes.string,
    text: PropTypes.string,
    handler: PropTypes.func,
    href: PropTypes.string,
    target: PropTypes.oneOf(['_self', '_blank', '_parent', '_top']),
    qaId: PropTypes.string,
  }),
  qaId: PropTypes.string,
  onDismiss: PropTypes.func.isRequired,
};

Toast.defaultProps = {
  type: 'information',
  onDismiss: () => {},
};



// WEBPACK FOOTER //
// ./src/notifications/toast/index.jsx